import { IChartApi, ISeriesApi, LineSeries } from 'lightweight-charts';
import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { StockIndexPriceResponse } from '../../../server/response/responseScheme';
import { apiClient } from '../../utils/apiClient';
import { useMobxInstance } from '../../utils/mobxUtil';
import StockChart, { StockChartRef } from '../chart/StockChart';
import ChangeValue from '../common/ChangeValue';

class ViewStore {
    krwData: StockIndexPriceResponse[] = [];
    usdData: StockIndexPriceResponse[] = [];
    type: 'krw' | 'usd' = 'krw';
    isLoading: boolean = true;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        const [krwResponse, usdResponse] = await Promise.all([
            apiClient.stockIndex.public.getValuesInRange.$query({
                ticker: '_USDKRW',
            }),
            apiClient.stockIndex.public.getValuesInRange.$query({
                ticker: '_USD',
            }),
        ]);

        runInAction(() => {
            this.krwData = krwResponse;
            this.usdData = usdResponse;
            this.isLoading = false;
        });
    }

    changeType(type: 'krw' | 'usd') {
        this.type = type;
    }
}

const CurrencyBriefCard = observer(({ className }: { className?: string }) => {
    const viewStore = useMobxInstance(ViewStore);

    const chartRef = useRef<StockChartRef>(null);
    const currentSeriesRef = useRef<ISeriesApi<any> | null>(null);

    const redrawSeries = useCallback(() => {
        const chart = chartRef.current?.getChartApi();
        if (!chart) {
            return;
        }

        if (currentSeriesRef.current) {
            chart.removeSeries(currentSeriesRef.current);
        }

        if (viewStore.type === 'krw') {
            const krwSeries = chart.addSeries(LineSeries, {
                lineWidth: 2,
                priceScaleId: 'right',
            });

            krwSeries.setData(
                _.map(viewStore.krwData, (value) => ({
                    time: value.date,
                    value: value.value,
                })),
            );

            chart.timeScale().setVisibleLogicalRange({
                from: viewStore.krwData.length - 80,
                to: viewStore.krwData.length,
            });

            currentSeriesRef.current = krwSeries;
        } else if (viewStore.type === 'usd') {
            const usdSeries = chart.addSeries(LineSeries, {
                lineWidth: 2,
                priceScaleId: 'right',
            });

            usdSeries.setData(
                _.map(viewStore.usdData, (value) => ({
                    time: value.date,
                    value: value.value,
                })),
            );

            chart.timeScale().setVisibleLogicalRange({
                from: viewStore.usdData.length - 80,
                to: viewStore.usdData.length,
            });

            currentSeriesRef.current = usdSeries;
        }
    }, [viewStore.type]);

    const onChartReady = async (chart: IChartApi) => {
        await viewStore.fetch();
    };

    useEffect(() => {
        if (chartRef.current && !viewStore.isLoading) {
            redrawSeries();
        }
    }, [viewStore.type, viewStore.isLoading]);

    const lastKrwPoint = _.nth(viewStore.krwData, -1);
    const prevKrwPoint = _.nth(viewStore.krwData, -2);
    const krwChange =
        lastKrwPoint?.value && prevKrwPoint?.value ? lastKrwPoint.value - prevKrwPoint.value : 0;

    const lastUsdPoint = _.nth(viewStore.usdData, -1);
    const prevUsdPoint = _.nth(viewStore.usdData, -2);
    const usdChange =
        lastUsdPoint?.value && prevUsdPoint?.value ? lastUsdPoint.value - prevUsdPoint.value : 0;

    return (
        <div className={twMerge('flex grow flex-col', className)}>
            <div className="-mx-5">
                <StockChart ref={chartRef} onChartReady={onChartReady} />
            </div>
            {viewStore.type === 'krw' && lastKrwPoint && (
                <div className="mt-4">
                    <div className="text-base-content/80 flex flex-col text-xl">
                        <div>
                            <span className="text-2xl font-bold">원화 환율</span>은
                        </div>
                        <div className="mt-3">
                            1달러 당
                            <strong className="mr-2 ml-2 text-3xl font-semibold">
                                {lastKrwPoint.value?.toFixed(2)}원
                            </strong>
                            으로
                        </div>
                        <div className="mt-3">
                            <ChangeValue
                                className="mr-2 text-3xl font-semibold"
                                value={krwChange}
                                sign={true}
                                round={2}
                                suffix="원"
                            />
                            {krwChange > 0 ? '상승' : '하락'}했습니다.
                        </div>
                    </div>
                </div>
            )}
            {viewStore.type === 'usd' && (
                <div className="mt-4">
                    {lastUsdPoint && (
                        <div className="text-base-content/80 flex flex-col text-xl">
                            <div>
                                <span className="text-2xl font-bold">달러 인덱스</span>는
                            </div>
                            <div className="mt-3">
                                <strong className="mr-2 ml-2 text-3xl font-semibold">
                                    {lastUsdPoint.value?.toFixed(2)}
                                </strong>
                                으로
                            </div>
                            <div className="mt-3">
                                <ChangeValue
                                    className="mr-2 text-3xl font-semibold"
                                    value={usdChange}
                                    sign={true}
                                    round={2}
                                    suffix="포인트"
                                />
                                {usdChange > 0 ? '상승' : '하락'}했습니다.
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="mt-8 flex items-center gap-3">
                <button
                    className={twMerge(
                        'btn text-base-content/40 rounded-full border',
                        viewStore.type === 'krw' &&
                            'btn-soft bg-primary/10 text-primary border-primary',
                    )}
                    onClick={() => viewStore.changeType('krw')}
                >
                    원화 환율
                </button>
                <button
                    className={twMerge(
                        'btn text-base-content/40 rounded-full border',
                        viewStore.type === 'usd' &&
                            'btn-soft bg-primary/10 text-primary border-primary',
                    )}
                    onClick={() => viewStore.changeType('usd')}
                >
                    달러 인덱스
                </button>
            </div>
        </div>
    );
});

export default CurrencyBriefCard;
